import { useCallback, useEffect, useState } from 'react';
import Spinner, { SpinnerSize } from '../Spinner/Spinner';
import { useTranslation } from 'react-i18next';
import { SignedUrl } from '@/data/models/signedUrl';
import './PrsImage.scss';

interface ImageProps {
  signedUrl: SignedUrl | undefined;
  alt?: string;
}

const tiffExtensions = ['.tif', '.tiff'];
const tiffMimeTypes = new Set([
  'image/tif',
  'image/tiff',
  'image/x-tiff',
  'application/octet-stream',
]);

function hasTiffExtension(value?: string | null) {
  if (!value) return false;

  const path = value.split(/[?#]/)[0].toLowerCase();
  return tiffExtensions.some((extension) => path.endsWith(extension));
}

function hasTiffMimeType(value?: string | undefined) {
  if (!value) return false;

  return tiffMimeTypes.has(value);
}

function isTiffContentType(contentType: string | null) {
  if (!contentType) return false;

  return tiffMimeTypes.has(contentType.split(';')[0].trim().toLowerCase());
}

async function convertTiffToDataUrl(
  url: string,
  signal: AbortSignal,
  requireTiffContentType: boolean
) {
  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(`Unable to load image: ${response.status}`);
  }

  if (
    requireTiffContentType &&
    !isTiffContentType(response.headers.get('content-type'))
  ) {
    throw new Error('Image response is not TIFF');
  }

  const buffer = await response.arrayBuffer();
  const { default: Tiff } = await import('tiff.js');
  return new Tiff({ buffer }).toCanvas().toDataURL();
}

export default function PrsImage({ signedUrl, alt }: ImageProps) {
  const [displayUrl, setDisplayUrl] = useState<string>();
  const [attemptedTiffFallback, setAttemptedTiffFallback] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();

  const shouldConvertTiff =
    hasTiffMimeType(signedUrl?.mimeType) || hasTiffExtension(signedUrl?.url);

  useEffect(() => {
    const abortController = new AbortController();
    setLoadError(false);
    setAttemptedTiffFallback(false);

    if (!signedUrl?.url) {
      setDisplayUrl(undefined);
      setLoading(false);
      return () => abortController.abort();
    }

    if (!shouldConvertTiff) {
      setDisplayUrl(signedUrl.url);
      setLoading(false);
      return () => abortController.abort();
    }

    setDisplayUrl(undefined);
    setLoading(true);

    convertTiffToDataUrl(signedUrl.url, abortController.signal, false)
      .then((dataUrl) => setDisplayUrl(dataUrl))
      .catch(() => {
        if (!abortController.signal.aborted) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });

    return () => abortController.abort();
  }, [signedUrl, shouldConvertTiff]);

  const tryTiffFallback = useCallback(() => {
    if (!signedUrl?.url || attemptedTiffFallback) {
      setLoadError(true);
      return;
    }

    const abortController = new AbortController();
    setAttemptedTiffFallback(true);
    setLoading(true);

    convertTiffToDataUrl(signedUrl.url, abortController.signal, true)
      .then((dataUrl) => {
        setDisplayUrl(dataUrl);
        setLoadError(false);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [attemptedTiffFallback, signedUrl]);

  return (
    <div>
      {loading ? (
        <Spinner cover={true} size={SpinnerSize.FullComponent} />
      ) : loadError ? (
        <div className="image-load-error">
          {t('upload.image_unable_to_load')}
        </div>
      ) : (
        <img
          alt={alt}
          src={displayUrl}
          onError={shouldConvertTiff ? undefined : tryTiffFallback}
        />
      )}
    </div>
  );
}
